import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileText, Loader2, Mail, Printer, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { sendReportByEmail, type PdfReportDef, type ReportOpenOptions } from '@/api/reports';
import type { UserListItem } from '@/api/users';

export default function PdfModal({
  title,
  url,
  filename,
  users,
  report,
  options,
  onClose,
}: {
  title: string;
  url: string;
  filename: string;
  users: UserListItem[];
  report: PdfReportDef;
  options?: ReportOpenOptions;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [mailSending, setMailSending] = useState(false);

  const activeUsers = useMemo(
    () => users.filter((user) => user.is_active === 1 && user.email?.trim()),
    [users],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSendMail = async () => {
    if (!selectedEmail) return;
    setMailSending(true);
    try {
      await sendReportByEmail(report.id, selectedEmail, options, options?.extraParams);
      toast.success('Rapor mail kuyruğuna alındı.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Rapor maili gönderilemedi.';
      toast.error(message);
    } finally {
      setMailSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={ref} className="flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ width: '90vw', height: '90vh' }}>
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b bg-gray-50 flex-shrink-0">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 text-rose-600" />
            <span className="truncate font-semibold text-gray-800 text-sm">{title}</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <select
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              className="h-9 min-w-[220px] rounded-lg border border-amber-200 bg-white px-3 text-xs text-gray-700 outline-none transition focus:border-amber-400"
            >
              <option value="">E-posta seç</option>
              {activeUsers.map((user) => (
                <option key={user.user_uuid_id} value={user.email}>
                  {(user.fullname || user.username) + ' - ' + user.email}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSendMail}
              disabled={!selectedEmail || mailSending}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              Gönder
            </button>
            <button type="button" onClick={() => frameRef.current?.contentWindow?.print()} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600"><Printer size={14}/>Yazdır</button>
            <a
              href={url}
              download={filename}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> İndir
            </a>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <iframe ref={frameRef} src={url} className="flex-1 w-full" title={title} />
      </div>
    </div>
  );
}

